import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { fetchUserNames } from "../../store/slices/userSlice";
import {
  fetchEmployeeDetails,
  updateEmployeeField,
} from "../../store/slices/employeeSlice";
import {
  fetchPayrollForMonth,
  resetPayrollState,
} from "../../store/slices/payrollSlice";
import { ChevronDown } from "lucide-react";
import SalaryTable from "./SalaryTable";
import toast from "react-hot-toast";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const generateYearOptions = () => {
  const years: number[] = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);
  return years;
};

const SelectInput: React.FC<{
  label: string;
  value: string | number;
  onChange: (value: number | "") => void;
  options: { value: string | number; label: string }[];
}> = ({ label, value, onChange, options }) => (
  <div className="relative">
    <label className="block text-xs font-medium text-gray-700 mb-1">
      {label}
    </label>
    <select
      value={value}
      onChange={(e) =>
        onChange(e.target.value === "" ? "" : Number(e.target.value))
      }
      className="appearance-none w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm"
    >
      <option value="">Select {label}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <ChevronDown className="absolute right-2 top-8 w-4 h-4 text-gray-400" />
  </div>
);

const NumberInput: React.FC<{
  label: string;
  value: number;
  onChange: (value: number) => void;
  onBlur: (value: number) => void;
  disabled: boolean;
}> = ({ label, value, onChange, onBlur, disabled }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">
      {label}
    </label>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      onBlur={(e) => onBlur(Number(e.target.value))}
      disabled={disabled}
      className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm ${
        disabled ? "bg-gray-100 cursor-not-allowed" : ""
      }`}
    />
  </div>
);

const payrollRows = [
  {
    label: "Basic Salary",
    accessor: (data: any) => ({
      actual: data.earnings.basicSalary || 0,
      master: data.earnings.basicSalary || 0,
    }),
  },
  {
    label: "Dearness Allowance",
    accessor: (data: any) => ({
      actual: data.earnings.dearnessAllowance || 0,
      master: data.earnings.dearnessAllowance || 0,
    }),
  },
  {
    label: "House Rent Allowance",
    accessor: (data: any) => ({
      actual: data.earnings.houseRentAllowance || 0,
      master: data.earnings.houseRentAllowance || 0,
    }),
  },
  {
    label: "Leave Travel Allowance",
    accessor: (data: any) => ({
      actual: data.earnings.leaveTravelAllowance || 0,
      master: data.earnings.leaveTravelAllowance || 0,
    }),
  },
  {
    label: "Special Allowances",
    accessor: (data: any) => ({
      actual: data.earnings.specialAllowance || 0,
      master: data.earnings.specialAllowance || 0,
    }),
  },
  {
    label: "Gross Salary (A)",
    accessor: (data: any) => ({
      actual:
        (data.earnings.basicSalary || 0) +
        (data.earnings.dearnessAllowance || 0) +
        (data.earnings.houseRentAllowance || 0) +
        (data.earnings.leaveTravelAllowance || 0) +
        (data.earnings.specialAllowance || 0),
      master:
        (data.earnings.basicSalary || 0) +
        (data.earnings.dearnessAllowance || 0) +
        (data.earnings.houseRentAllowance || 0) +
        (data.earnings.leaveTravelAllowance || 0) +
        (data.earnings.specialAllowance || 0),
    }),
    isTotal: true,
  },
  {
    label: "Employee Benefits",
    accessor: () => ({ actual: "", master: "" }),
    isHeader: true,
  },
  {
    label: "*Employee Contribution to Provident Fund",
    accessor: (data: any) => ({
      actual: data.deductions.employeeProvidentFund || 0,
      master: data.deductions.employeeProvidentFund || 0,
    }),
  },
  {
    label: "*Employee Contribution to ESIC",
    accessor: (data: any) => ({
      actual: data.deductions.employeeEsicContribution || 0,
      master: data.deductions.employeeEsicContribution || 0,
    }),
  },
  {
    label: "Professional Tax",
    accessor: (data: any) => ({
      actual: data.deductions.professionalTax || 0,
      master: data.deductions.professionalTax || 0,
    }),
  },
  {
    label: "Total Deductions (B)",
    accessor: (data: any) => ({
      actual: data.totalDeductions || 0,
      master: data.totalDeductions || 0,
    }),
    isTotal: true,
  },
  {
    label: "**Net Salary on Hand (A-B)",
    accessor: (data: any) => ({
      actual: data.netSalary || 0,
      master: data.netSalary || 0,
    }),
    isTotal: true,
  },
  {
    label: "Company Benefits",
    accessor: () => ({ actual: "", master: "" }),
    isHeader: true,
  },
  {
    label: "*Employer Contribution to Provident Fund",
    accessor: (data: any) => ({
      actual: data.employerContributions.employerProvidentFund || 0,
      master: data.employerContributions.employerProvidentFund || 0,
    }),
  },
  {
    label: "*Employer Contribution to ESIC",
    accessor: (data: any) => ({
      actual: data.employerContributions.employerEsicContribution || 0,
      master: data.employerContributions.employerEsicContribution || 0,
    }),
  },
  {
    label: "Gratuity",
    accessor: (data: any) => ({
      actual: data.employerContributions.gratuity || 0,
      master: data.employerContributions.gratuity || 0,
    }),
  },
  {
    label: "Total (C)",
    accessor: (data: any) => ({
      actual: data.totalEmployerCost || 0,
      master: data.totalEmployerCost || 0,
    }),
    isTotal: true,
  },
  {
    label: "Cost to Company (CTC) (A+C)",
    accessor: (data: any) => ({
      actual: data.costToCompany || 0,
      master: data.costToCompany || 0,
    }),
    isTotal: true,
    highlight: true,
  },
];

const PayrollPage: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const users = useSelector((state: RootState) => state.users.allUsersNames);
  const employeeDetails = useSelector(
    (state: RootState) => state.employee.userDetails
  );
  const payrollData = useSelector((state: RootState) => state.payroll.data);
  const allUsersPayroll = useSelector(
    (state: RootState) => state.payroll.allUsersPayroll
  );
  const payrollError = useSelector((state: RootState) => state.payroll.error);

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<typeof users>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [month, setMonth] = useState<number | "">("");
  const [year, setYear] = useState<number | "">("");
  const [monthlyGross, setMonthlyGross] = useState<number>(0);
  const [yearlyCTC, setYearlyCTC] = useState<number>(0);
  const [allUsersPayrollData, setAllUsersPayrollData] = useState<any[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const isEditable = !!selectedUserId;

  useEffect(() => {
    dispatch(fetchUserNames());
  }, [dispatch]);

  useEffect(() => {
    if (selectedUserId) dispatch(fetchEmployeeDetails(selectedUserId));
  }, [dispatch, selectedUserId]);

  useEffect(() => {
    if (employeeDetails?.grossSalaryMonthly) {
      setMonthlyGross(Math.round(employeeDetails.grossSalaryMonthly));
      setYearlyCTC(Math.round(employeeDetails.grossSalaryMonthly * 12));
    } else {
      setMonthlyGross(0);
      setYearlyCTC(0);
    }
  }, [employeeDetails]);

  useEffect(() => {
    if (selectedUserId && month && year) {
      console.log(
        `Fetching single-user payroll for user: ${selectedUserId}, month: ${month}, year: ${year}`
      );
      dispatch(fetchPayrollForMonth({ userId: selectedUserId, month, year }));
    }
  }, [dispatch, selectedUserId, month, year]);

  useEffect(() => {
    if (allUsersPayroll) {
      console.log("allUsersPayroll updated:", allUsersPayroll);
      const validPayrollResults = allUsersPayroll
        .filter((userData: any) => !userData.error && userData.payroll)
        .map((userData: any) => ({
          userId: userData.userId,
          username: userData.username,
          data: userData.payroll,
          monthlyGross: userData.grossSalaryMonthly || 0,
        }));
      setAllUsersPayrollData(validPayrollResults);
      console.log("allUsersPayrollData set:", validPayrollResults);
    } else {
      setAllUsersPayrollData([]);
    }
  }, [allUsersPayroll]);

  useEffect(() => {
    // console.log("payrollData:", payrollData);
    // console.log("payrollError:", payrollError);
  }, [payrollData, payrollError]);

  const processSalary = async () => {
    if (!month || !year) {
      toast.error("Please select both month and year first");
      return;
    }

    setIsProcessing(true);
    try {
      const userIds = users.map((user) => user._id);
      console.log(
        `Processing salary for users: ${userIds}, month: ${month}, year: ${year}`
      );
      const result = await dispatch(
        fetchPayrollForMonth({ userIds, month, year })
      ).unwrap();

      const validPayrollResults = result
        .filter((userData: any) => !userData.error && userData.payroll)
        .map((userData: any) => ({
          userId: userData.userId,
          username: userData.username,
          data: userData.payroll,
          monthlyGross: userData.grossSalaryMonthly || 0,
        }));

      result
        .filter((userData: any) => userData.error)
        .forEach((userData: any, index: number) => {
          setTimeout(() => {
            toast.error(
              `Payroll error for ${userData.username || userData.userId}: ${
                userData.error
              }`,
              { duration: 4000 }
            );
          }, index * 1000);
        });

      setAllUsersPayrollData(validPayrollResults);

      if (selectedUserId) {
        console.log(
          `Re-fetching single-user payroll for user: ${selectedUserId}, month: ${month}, year: ${year}`
        );
        await dispatch(
          fetchPayrollForMonth({ userId: selectedUserId, month, year })
        );
      }
    } catch (error: any) {
      console.error("Failed to fetch batch payroll data:", error);
      toast.error(error || "Failed to process payroll data");
      setAllUsersPayrollData([]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    let results = [...users];
    if (value.trim() !== "") {
      results = results.filter((u) =>
        u.username.toLowerCase().includes(value.toLowerCase())
      );
    }
    results.sort((a, b) => a.username.localeCompare(b.username));
    setFilteredUsers(results);
  };

  const handleUserSelect = (userId: string, username: string) => {
    setSelectedUserId(userId);
    setSearchTerm(username);
    setFilteredUsers([]);
  };

  const clearUser = () => {
    setSelectedUserId(null);
    setSearchTerm("");
    setFilteredUsers([]);
    setMonth("");
    setYear("");
    setMonthlyGross(0);
    setYearlyCTC(0);
    dispatch(resetPayrollState());
  };

  const handleGrossUpdate = async (
    value: number,
    type: "monthly" | "yearly"
  ) => {
    if (!selectedUserId) return;

    let newMonthly = value;
    let newYearly = value;

    if (type === "yearly") {
      newMonthly = Math.round(value / 12);
      newYearly = value;
    } else {
      newYearly = Math.round(value * 12);
    }

    if (newMonthly === monthlyGross && newYearly === yearlyCTC) return;

    setMonthlyGross(newMonthly);
    setYearlyCTC(newYearly);

    await dispatch(
      updateEmployeeField({
        userId: selectedUserId,
        fieldKey: "grossSalaryMonthly",
        value: newMonthly,
      })
    );

    if (month && year) {
      console.log(
        `Refetching payroll after gross update for user: ${selectedUserId}`
      );
      dispatch(fetchPayrollForMonth({ userId: selectedUserId, month, year }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-md p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
        Payroll Calculation
      </h2>
      <div className="flex items-center gap-4 mb-4">
        <div className="relative w-full max-w-xs">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => {
              if (!selectedUserId) {
                const allUsers = [...users].sort((a, b) =>
                  a.username.localeCompare(b.username)
                );
                setFilteredUsers(allUsers);
              }
            }}
            placeholder="Search employee..."
            disabled={!!selectedUserId}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none"
          />
          {selectedUserId && (
            <button
              onClick={clearUser}
              className="absolute right-3 top-3.5 text-gray-400 hover:text-red-500 text-lg"
            >
              &#10005;
            </button>
          )}
          {filteredUsers.length > 0 && (
            <ul className="absolute z-10 bg-white border border-gray-300 w-full mt-1 max-h-40 overflow-y-auto rounded shadow">
              {filteredUsers.map((u) => (
                <li
                  key={u._id}
                  className="px-3 py-2 cursor-pointer hover:bg-blue-50"
                  onClick={() => handleUserSelect(u._id, u.username)}
                >
                  {u.username}
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          onClick={processSalary}
          disabled={isProcessing}
          className={`rounded-lg px-3 py-2.5 text-sm transition-all font-medium 
            ${
              isProcessing
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-navyBlue-primary text-white hover:bg-blue-500 active:scale-95 focus:ring-2 focus:ring-blue-300"
            } max-w-xs w-full`}
        >
          {isProcessing ? "Processing..." : "Process Salary"}
        </button>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-4 items-end">
        <SelectInput
          label="Month"
          value={month}
          onChange={setMonth}
          options={monthNames.map((name, idx) => ({
            value: idx + 1,
            label: name,
          }))}
        />
        <SelectInput
          label="Year"
          value={year}
          onChange={setYear}
          options={generateYearOptions().map((y) => ({
            value: y,
            label: y.toString(),
          }))}
        />
        <div className="col-span-2 grid grid-cols-2 gap-2">
          <NumberInput
            label="Gross CTC (Yearly)"
            value={yearlyCTC}
            onChange={setYearlyCTC}
            onBlur={(value) => handleGrossUpdate(value, "yearly")}
            disabled={!isEditable}
          />
          <NumberInput
            label="Monthly Gross"
            value={monthlyGross}
            onChange={setMonthlyGross}
            onBlur={(value) => handleGrossUpdate(value, "monthly")}
            disabled={!isEditable}
          />
        </div>
      </div>

      {selectedUserId && (!month || !year) && (
        <div className="mt-6 text-gray-600">
          Please select both month and year to view payroll details.
        </div>
      )}
      {selectedUserId && month && year && payrollError && (
        <div className="mt-6 text-red-500">Error: {payrollError}</div>
      )}
      {selectedUserId &&
        month &&
        year &&
        !payrollData?.earnings &&
        !payrollError && (
          <div className="mt-6 text-gray-600">
            No payroll data available for {searchTerm}.
          </div>
        )}
      {selectedUserId && payrollData?.earnings && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Payroll Details for {searchTerm}
          </h3>
          <table className="w-full text-sm border">
            <thead>
              <tr className="bg-gray-50">
                <th className="border px-3 py-2 text-left">Particulars</th>
                <th className="border px-3 py-2 text-right">Actual Monthly</th>
                <th className="border px-3 py-2 text-right">Master Monthly</th>
                <th className="border px-3 py-2 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {payrollRows.map((row, index) => (
                <tr
                  key={index}
                  className={
                    row.isTotal
                      ? "font-semibold" + (row.highlight ? " bg-gray-100" : "")
                      : row.isHeader
                      ? "font-semibold bg-gray-50"
                      : ""
                  }
                >
                  <td className="border px-3 py-2">{row.label}</td>
                  <td className="border px-3 py-2 text-right">
                    {row.isHeader
                      ? ""
                      : row.accessor(payrollData).actual.toLocaleString()}
                  </td>
                  <td className="border px-3 py-2 text-right">
                    {row.isHeader
                      ? ""
                      : row.accessor(payrollData).master.toLocaleString()}
                  </td>
                  <td className="border px-3 py-2 text-right text-xs text-gray-600">
                    {(() => {
                      const actual = row.accessor(payrollData).actual;
                      if (row.isHeader || actual === "" || actual === 0)
                        return "";
                      const gross = payrollRows[5].accessor(payrollData).actual;
                      return gross > 0
                        ? ((actual / gross) * 100).toFixed(1) + "%"
                        : "0.0%";
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {allUsersPayrollData.length > 0 && month && year && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            All Users Salary for {monthNames[(month as number) - 1]} {year}
          </h3>
          <SalaryTable allUsersPayroll={allUsersPayrollData} />{" "}
          {/* Removed month, year props */}
        </div>
      )}
    </div>
  );
};

export default PayrollPage;
